import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trophy, AlertCircle, RotateCcw } from "lucide-react";
import { useAttemptStore } from "@/store/attemptStore";
import type { LeaderboardResponse } from "@/types/simulation";

interface Props {
  simId: string;
  simTitle: string;
  onClose: () => void;
}

function rankAccent(rank: number): { color: string; bg: string; label: string } {
  if (rank === 1) return { color: "#FCD34D", bg: "rgba(252,211,77,0.14)", label: "🥇" };
  if (rank === 2) return { color: "#E5E7EB", bg: "rgba(229,231,235,0.12)", label: "🥈" };
  if (rank === 3) return { color: "#FBBF24", bg: "rgba(251,191,36,0.12)", label: "🥉" };
  return { color: "#A78BFA", bg: "rgba(167,139,250,0.10)", label: `#${rank}` };
}

/**
 * LeaderboardModal — opens from the sim card on the simulations list.
 * Shows top-10 best-score-per-learner (server-scoped to the same client_id),
 * highlights the current user, and shows their position when they're outside
 * the top 10. Gated server-side on the sim's `LeaderboardEnabled` setting.
 */
export function LeaderboardModal({ simId, simTitle, onClose }: Props): JSX.Element {
  const { fetchLeaderboard } = useAttemptStore();
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchLeaderboard(simId);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the leaderboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simId]);

  // Esc to close
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // With LIMIT removed the user is always in `entries` if they have any
  // completed attempts. The "you" row outside the list is only ever shown
  // as a defensive fallback when entries are present but the user isn't
  // among them (shouldn't happen in normal flow now).
  const userInList = data?.entries.some((e) => e.isYou) ?? false;

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0c0e22] to-[#070818] p-6 text-white shadow-2xl"
        >
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-400" />

          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full"
                style={{ background: "linear-gradient(135deg, #8B5CF6, #22D3EE)", boxShadow: "0 0 18px rgba(139,92,246,0.5)" }}
              >
                <Trophy className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-fuchsia-300">Leaderboard</div>
                <h2 className="text-lg font-bold leading-tight">{simTitle}</h2>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-white/50 transition-colors hover:bg-white/5 hover:text-white"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-14">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
                className="h-9 w-9 rounded-full border-2 border-white/10 border-t-fuchsia-400"
              />
              <p className="mt-4 text-xs text-white/50">Loading the leaderboard…</p>
            </div>
          )}

          {!loading && error !== null && (
            <div className="flex flex-col items-center rounded-xl border border-neusim-band-low/30 bg-neusim-band-low/5 px-6 py-10 text-center mt-5">
              <AlertCircle className="h-8 w-8 text-[#f87272]" />
              <p className="mt-3 text-sm text-white/70">{error}</p>
              <button
                type="button"
                onClick={() => void load()}
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white hover:border-white/30 hover:bg-white/10"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Try again
              </button>
            </div>
          )}

          {!loading && error === null && data !== null && (
            <div className="mt-5">
              {data.entries.length === 0 ? (
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-8 text-center">
                  <p className="text-sm text-white/55">
                    No completed attempts in your cohort yet. Be the first on the board.
                  </p>
                </div>
              ) : (
                <ul className="max-h-[55vh] space-y-1.5 overflow-y-auto pr-1">
                  {data.entries.map((e) => {
                    const accent = rankAccent(e.rank);
                    return (
                      <motion.li
                        key={e.userId + e.rank}
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 + e.rank * 0.03 }}
                        className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 ${e.isYou ? "border-fuchsia-400/40 bg-fuchsia-500/10" : "border-white/10 bg-white/[0.02]"}`}
                      >
                        <span
                          className="flex h-8 w-10 shrink-0 items-center justify-center rounded-lg text-[13px] font-bold"
                          style={{ background: accent.bg, color: accent.color }}
                        >
                          {accent.label}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-white">
                            {e.fullName}
                            {e.isYou && <span className="ml-2 rounded-full bg-fuchsia-500/25 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-fuchsia-200">You</span>}
                          </p>
                        </div>
                        <span className="shrink-0 font-mono text-base font-bold text-white">{e.score}</span>
                      </motion.li>
                    );
                  })}
                </ul>
              )}

              {/* Defensive fallback — only fires if `entries` somehow doesn't include the user despite a recorded best. */}
              {data.yours !== null && !userInList && data.entries.length > 0 && (
                <div className="mt-4 border-t border-white/10 pt-3">
                  <div className="flex items-center gap-3 rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/10 px-3.5 py-2.5">
                    <span
                      className="flex h-8 w-10 shrink-0 items-center justify-center rounded-lg text-[13px] font-bold"
                      style={{ background: "rgba(167,139,250,0.15)", color: "#A78BFA" }}
                    >
                      #{data.yours.rank}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white">
                        You
                        <span className="ml-2 rounded-full bg-fuchsia-500/25 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-fuchsia-200">
                          outside top 10
                        </span>
                      </p>
                    </div>
                    <span className="shrink-0 font-mono text-base font-bold text-white">{data.yours.score}</span>
                  </div>
                </div>
              )}

              <p className="mt-5 text-center text-[11px] text-white/35">
                {data.entries.length === 1
                  ? "1 participant in your cohort so far."
                  : `${data.entries.length} participants in your cohort. Ranks reflect each learner's best completed attempt.`}
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
